import React, { useState, useEffect, useRef } from 'react';
import { Paper, Box, Autocomplete, TextField, Radio, RadioGroup, FormControlLabel, Button, Alert, Snackbar, Typography, CircularProgress, Backdrop, FormControl, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { axiosRun } from "../utils/axiosWrap";
import { config } from "../config";
import ReCAPTCHA from 'react-google-recaptcha';

const ApplicantRequestNew = () => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [documents, setDocuments] = useState([]);
    const [specialities, setSpecialities] = useState([]);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [consent, setConsent] = useState(false);
    const recaptchaRef = useRef(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const currentLanguage = i18n.language;
    console.log("current language ", currentLanguage);
    // Fetch data from API on component mount
    useEffect(() => {
        const getSpecialities = async () => {
            const data = await axiosRun(config.apiLinks.getSpecialityForStudying, 'GET');
            const result = data.flatMap(item => {
                if (item.Specializations.length === 0) {
                    item.Specializations = null;
                } else if (item.Specializations.length === 1) {
                    return [{ ...item, Specializations: { ...item.Specializations[0] } }];
                } else if (item.Specializations.length > 1) {
                    return item.Specializations.map((spec, index) => ({
                        ...item,
                        id: `${item.id}-${index + 1}`,
                        Specializations: { ...spec },
                    }));
                }
                return [item];
            });
            setSpecialities(result);
        };

        const getDocuments = async () => {
            try {
                const data = await axiosRun(config.apiLinks.getApplicantsDocumentsList, 'GET');
                setDocuments(data);
            } catch (error) {
                console.error('Error fetching documents:', error);
            }
        };

        getDocuments();
        getSpecialities();
    }, []);

    // Handle autocomplete change
    const handleAutocompleteChange = (event, value) => {
        setSelectedItems(value);
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setOpenSnackbar(false);
    };

    // Handle radio button change
    const handleRadioChange = (event) => {
        setSelectedDocument(event.target.value);
    };

    // Handle text field changes
    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    };

    const handlePhoneChange = (event) => {
        setPhone(event.target.value);
    };

    const handleConsentChange = (event) => {
        setConsent(event.target.checked);
    };

    // Handle save button click
    const handleSave = async () => {

        if (!consent) {
            setSnackbarMessage(
              currentLanguage === 'en'
                ? "You must agree to provide your email and phone"
                : "Необхідно дати згоду на надання email та телефону"
            );
            setSnackbarSeverity('warning'); // або 'error'
            setOpenSnackbar(true);
            return;
        }

        if (!captchaToken) {
            setSnackbarMessage(t('applicant.captchaRequired') || "Необхідно пройти reCAPTCHA");
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            return;
        }
        setIsLoading(true);
        try {
            const selectedDoc = documents.find(doc => doc.id === selectedDocument);
            const docData = selectedDocument ? { [selectedDocument]: true } : {};
            const data = await axiosRun(config.apiLinks.createNewApplicantRequest, 'PUT', {
                specialities: selectedItems,
                token: captchaToken,
                documents: docData,
                email: email,
                phone: phone
            });
            console.log('Data submitted successfully:', data);
            setCaptchaToken(null);
            recaptchaRef.current.reset();
            window.location.replace(`/applicant/item/${data.redirectToHash}`);
        } catch (error) {
            console.error('Error submitting data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle CAPTCHA verification
    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    return (
        <Box sx={{ margin: { xs: '0 9%', sm: '0 30%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ backgroundColor: 'success.main', padding: 2, borderRadius: 1, maxWidth: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <img
                    src="https://snau.edu.ua/wp-content/themes/smartuniversity/assets/images/default/logo.png"
                    alt="University Logo"
                    style={{ maxWidth: '150px', borderRadius: '50%', objectFit: 'contain', clipPath: 'circle()' }}
                />
                <Typography variant="body1" sx={{ color: 'white', marginTop: 1, textAlign: 'center' }}>
                    {t('applicant.SNAU_NAME_TEXT')}
                </Typography>
            </Box>
            <Paper elevation={3} sx={{ padding: 2, backgroundColor: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
                <Alert severity="info" sx={{ width: '100%', backgroundColor: 'primary.main', color: 'white', '& .MuiAlert-icon': { color: 'white' }, mx: 2 }}>
                    {t('applicant.infoForApplicantHowToUse')}
                </Alert>
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                    flexWrap: 'wrap'
                }}>
                    <Autocomplete
                        multiple
                        options={specialities}
                        getOptionLabel={(option) =>
                            option.Specializations ? `${option.Specializations.name}-${option.name}` : option.name
                        }
                        value={selectedItems}
                        onChange={handleAutocompleteChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t("applicant.chooseSpeciality")}
                                sx={{ width: { xs: '100%', sm: '300px' } }}
                            />
                        )}
                        sx={{ width: { xs: '100%', sm: '300px' } }}
                    />
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        alignItems: { xs: 'flex-start', sm: 'center' }
                    }}>
                        {/*<FormControl component="fieldset">*/}
                        {/*    <RadioGroup*/}
                        {/*        value={selectedDocument}*/}
                        {/*        onChange={handleRadioChange}*/}
                        {/*        name="document-selection"*/}
                        {/*    >*/}
                        {/*        {documents.map((doc) => (*/}
                        {/*            <FormControlLabel*/}
                        {/*                key={doc.id}*/}
                        {/*                value={doc.id}*/}
                        {/*                control={<Radio />}*/}
                        {/*                label={currentLanguage === 'en' ? doc.nameEn : doc.nameUa}*/}
                        {/*            />*/}
                        {/*        ))}*/}
                        {/*    </RadioGroup>*/}
                        {/*</FormControl>*/}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            minWidth: { sm: '200px' }
                        }}>
                            <TextField
                                label={t("applicant.email")}
                                value={email}
                                onChange={handleEmailChange}
                                sx={{ width: { xs: '100%', sm: '200px' } }}
                            />
                            <TextField
                                label={t("applicant.phone")}
                                value={phone}
                                onChange={handlePhoneChange}
                                sx={{ width: { xs: '100%', sm: '200px' } }}
                            />
                        </Box>
                    </Box>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={config.captchaKey}
                        onChange={handleCaptchaChange}
                    />
                    <Box sx={{ width: '100%', px: 2, mt: 1 }}>
                        <FormControlLabel
                          control={
                              <Checkbox
                                checked={consent}
                                onChange={handleConsentChange}
                                color="primary"
                                sx={{
                                    '& .MuiSvgIcon-root': { fontSize: 28 }
                                }}
                              />
                          }
                          label={
                              <Typography variant="body1" sx={{ fontSize: '0.9rem', lineHeight: 1.4 }}>
                                  Я згоден(на) надати свій email та телефон для подальшої обробки
                              </Typography>
                          }
                          sx={{
                              alignItems: 'flex-start',
                              width: '100%',
                              '& .MuiFormControlLabel-label': {
                                  paddingTop: '4px'
                              }
                          }}
                        />
                    </Box>
                    <Button variant="contained" disabled={isLoading} color="primary" onClick={handleSave}>
                        {t("applicant.startShareDocs")}
                    </Button>
                </Box>
            </Paper>
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress color="primary" size={60} thickness={5} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        {t("applicant.sendingData")}...
                    </Typography>
                </Box>
            </Backdrop>
            <Snackbar
              open={openSnackbar}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                  severity={snackbarSeverity}
                  onClose={handleSnackbarClose}
                  sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ApplicantRequestNew;